import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import forEach from 'lodash/forEach';
import find from 'lodash/find';
import isEqual from 'lodash/isEqual';
import keys from 'lodash/keys';
import _ from 'lodash';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core';

import * as Constants from '../../../../../constants/charts';
import { AGGREGATED_DATA_TYPE } from '../../../../../constants/metrics';
import { getI18n } from "../../../../../utils/intl";

import Loading from "../../../../Loading";
import CompositeXYChart from '../../../CompositeXYChart';
import NoData from '../../../NoData';
import ChartLegend from '../../../ChartLegend';
import CustomLabelComponent from '../../../CustomLabelComponent';

import {
  getDateRangeData, getDisplayDate,
  getParamsFromQueryData, lineColors, lightShadeLineColors, getParamsForHistoricalData,
  clearHistoricalDataMutations,
  clearMetricsHoverState,
} from '../../../utils';
import {
  getEntityPermuteMap,
  getBucketPermuteIdAndNames,
  getSourcesIdsFromResponse,
  getDefaultSharedMutations,
  getMetricKey,
  getSourceFromMetricKey,
  getMetricFromMetricKey,
} from '../../../utils/permute';
import { getXValueTick } from '../../../utils/filter';

import { TooltipStyle } from '../../../ChartTooltip/index.theme';
import styles from './styles';

const ERRORS_METRIC_DEFAULT = 'default';
const ERRORS_METRIC_CUSTOM_DATE = 'customDate';
const ERRORS_METRIC_CUSTOM_WEEK = 'customWeek';

const ErrorRateChart = (props) => {
  const {
    isLoading,
    defaultErrorsResponse,
    fetchCustomDateMetricsData,
    errorsResponseFor30DaysAgo,
    errorsResponseFor52WeeksAgo,
    apiMap, appMap, orgMap,
    is30DaysDataLoading,
    is52WeeksDataLoading,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [state, updateState] = useState({
    sharedMutations: {},
    keyMetricDataTS: 0,
    key: '',
    metricEvent: '',
  });
  
  const setState = (value) => {
    if (typeof value === 'function') {
      updateState(currentValue => ({ ...currentValue, ...value(state) }));
    } else {
      updateState(currentValue => ({ ...currentValue, ...value }));
    }
  }

  const defaultErrorsResponseRef = useRef(defaultErrorsResponse);
  useEffect(() => {
    const entitiesMap = { apiMap, appMap, orgMap };
    const oldSourcesIds = (state.sharedMutations && keys(state.sharedMutations)) || [];
    const newSourcesIds = getSourcesIdsFromResponse(defaultErrorsResponse, 'errors', entitiesMap);

    const hasNewSourceIds = newSourcesIds.length > 0;
    // reset sharedMutations if sources changes
    if (hasNewSourceIds &&
      !isEqual(oldSourcesIds.sort(), newSourcesIds.sort())) {
      const sharedMutations = getDefaultSharedMutations(newSourcesIds, ERRORS_METRIC_DEFAULT);
      setState({
        sharedMutations,
        keyMetricDataTS: defaultErrorsResponse?.timeStamp ?? 0,
      });
    } else if (hasNewSourceIds && defaultErrorsResponse.timeStamp > state.keyMetricDataTS) {
      setState({
        sharedMutations: clearHistoricalDataMutations(state.sharedMutations),
        keyMetricDataTS: defaultErrorsResponse?.timeStamp ?? 0,
      });
    }
    defaultErrorsResponseRef.current = defaultErrorsResponse;
  }, [defaultErrorsResponse]);

  useEffect(() => {
    setState(
      prevState => {
        const { key, metricEvent } = prevState;
        const sharedMutations = prevState.sharedMutations || {};
        const sourceKey = metricEvent ? getSourceFromMetricKey(key) : key;
        const metricKey = getMetricFromMetricKey(key);
        const sourceMutation = (sharedMutations && sharedMutations[sourceKey]) || {};
        const metricsMutation = sourceMutation.metrics || {};
        const metricMutation = metricsMutation[metricKey];
        const defaultResponse = defaultErrorsResponse;
        if (defaultResponse && defaultResponse.query) {
          if ((metricKey === ERRORS_METRIC_CUSTOM_DATE) && metricMutation.show) {
            const params = getParamsForHistoricalData(
              defaultResponse.query,
              Constants.HISTORICAL_METRIC_30DAYS,
              defaultResponse.data.errors,
            );
            fetchCustomDateMetricsData(params);
          }
          if ((metricKey === ERRORS_METRIC_CUSTOM_WEEK) && metricMutation.show) {
            const params = getParamsForHistoricalData(
              defaultResponse.query,
              Constants.HISTORICAL_METRIC_52WEEKS,
              defaultResponse.data.errors,
            );
            fetchCustomDateMetricsData(params);
          }
        }
      },
    );
  }, [state.key, state.metricEvent])

  const getHistoricalChartDataFromResponse = (errorsResponse) => {
    const queryParams = getParamsFromQueryData(
      errorsResponse && errorsResponse.query,
    );
    const timerange = queryParams.timerange;
    const timeAgg = queryParams.timeAgg;
    const startDate = queryParams.startDate;
    const endDate = queryParams.endDate;
    // get dateRangeData passed, instead of calculating it
    const dateRangeData = getDateRangeData(
      timerange,
      timeAgg,
      startDate,
      endDate,
    );
    const historicalChartDataFromResponse = getErrorRateData(
      dateRangeData,
      errorsResponse,
    );
    return historicalChartDataFromResponse;
  };

  const getTooltipLabel = (error, permuteObject, dateTimeStr) => {
    let errorRate = getI18nStrings('noDataText');
    if (error && !_.isUndefined(error.errorRate)) {
      errorRate = `${(Math.round(error.errorRate * 100) / 100).toFixed(2)}%`;
    }
    const tooltipLabel = `${permuteObject.names.join(' > ')}\n\n${getI18nStrings('errorRateText')} : ${errorRate}\n${getI18nStrings('dateTimeFormat')} : \n ${dateTimeStr}`;
    return tooltipLabel;
  };

  const getI18nStrings = (key) => {
    let i18nString = '';
    if (!_.isUndefined(key)) {
      const strObj = {
        dateTimeFormat: 'label.analytics.date.time.format.text',
        noDataText: 'label.analytics.dashboard.noData.text',
        errorRateText: 'label.analytics.errorRateChart.errorRate.text',
      };
      i18nString = intl.getI18nMessage(strObj[key]);
    }
    return i18nString;
  };

  const getErrorRateData = (xAxisData, errorResponse) => {
    let maxYValue = 0;
    // This is a special check, even when there are no transactions for a given
    // duration, backend sends an entry for each date under errorsTs. If there is
    // a fix from backend, then this check needs to be removed.
    if ((errorResponse && errorResponse.data && errorResponse.data.errors) &&
      ((errorResponse.data.errors.buckets && errorResponse.data.errors.buckets.length === 0) ||
      (_.isEmpty(errorResponse.data.errors)))) {
      return [];
    }
    const permuteErrorsTs =
      errorResponse && errorResponse.data && errorResponse.data.errorsTs;
    const sortedPermuteErrorsTs =
      (permuteErrorsTs && permuteErrorsTs.sort((a, b) => (a.date > b.date ? 1 : -1))) || [];
    let totalErrorsBuckets = errorResponse && errorResponse.data &&
      errorResponse.data.errors ? errorResponse.data.errors.buckets : [];
    const entitiesStr = (errorResponse && errorResponse.query.buckets) || '';
    const permuteMap = getEntityPermuteMap(totalErrorsBuckets, entitiesStr, {
      apiMap,
      appMap,
      orgMap,
    });
    const chartData = [];
    const {
      timerange,
      timeAgg,
      startDate,
      endDate,
    } = getParamsFromQueryData(errorResponse && errorResponse.query);
    if (!entitiesStr) {
      totalErrorsBuckets = keys(permuteMap);
    }
    totalErrorsBuckets.forEach((errorsBucket, idx) => {
      const bucketPermuteObject = getBucketPermuteIdAndNames(errorsBucket, entitiesStr, {
        apiMap,
        appMap,
        orgMap,
      });
      const permuteId = bucketPermuteObject.permuteId;
      let permuteErrorBucket = {};
      const data = [];
      sortedPermuteErrorsTs.forEach(timeBasedErrors => {
        permuteErrorBucket = find(timeBasedErrors.buckets, bucket => {
          const permuteObject = getBucketPermuteIdAndNames(bucket, entitiesStr, {
            apiMap,
            appMap,
            orgMap,
          });
          return permuteObject.permuteId === permuteId;
        });
        if (!entitiesStr) {
          permuteErrorBucket = timeBasedErrors;
        }
        const currentPermuteObject = permuteMap[permuteId];
        const dateTimeStr =
          getDisplayDate(moment.utc(timeBasedErrors.date).toISOString(),
            timerange, timeAgg, startDate, endDate, 'tooltip');
        const yValue = permuteErrorBucket && permuteErrorBucket.errorRate ?
          permuteErrorBucket.errorRate : 0;
        const xValue = xAxisData &&
          getXValueTick(xAxisData.xAxisValuesTickMap, timeBasedErrors.date);
        const tooltipLabel =
          getTooltipLabel(permuteErrorBucket, currentPermuteObject, dateTimeStr);
        const dataObj = {
          x: xValue,
          y: yValue,
          label: tooltipLabel,
        };
        data.push(dataObj);
        maxYValue = yValue > maxYValue ? yValue : maxYValue;
      });
      // This check is in case of aggregated error rate data, just in this case legend
      // has to error rate from errors under response data
      const actualErrorsBucket = (errorResponse && errorResponse.data &&
        errorResponse.data.errors && _.isUndefined(errorResponse.data.errors.buckets))
        && permuteId === AGGREGATED_DATA_TYPE ?
        errorResponse.data.errors : errorsBucket;
      if (data.length) {
        chartData.push({
          ...actualErrorsBucket,
          data,
          customLabelComponent: createCustomLabelComponent(TooltipStyle),
          id: permuteId,
          names: permuteMap[permuteId].names,
          type: Constants.CHART_ELEMENT_LINE,
          colorIndex: idx,
        });
      }
    });
    return chartData;
  };

  const getLineChartsData = (errorData, metric) => {
    const lineCharts = [];
    forEach(errorData, item => {
      const { id, data, customLabelComponent } = item;
      let { colorIndex } = item;
      let pattern = '';
      let useLightShadedColor = false;
      const lineData = [];
      const mutations = state.sharedMutations;
      const sourceMutation = mutations[id] || {};
      if (sourceMutation && sourceMutation.colorIndex !== undefined) {
        colorIndex = sourceMutation.colorIndex;
      }
      const metricsMutation = sourceMutation.metrics || {};
      if (metric === ERRORS_METRIC_CUSTOM_DATE) {
        pattern = 'dashed';
      }
      if (metric === ERRORS_METRIC_CUSTOM_WEEK) {
        useLightShadedColor = true;
      }
      const metricMutation = metricsMutation[metric] || {};
      const highLight = !!metricMutation.hover;
      if (!sourceMutation.show || !metricMutation.show) {
        return;
      }
      forEach(data, dataItem => {
        const { x, y, label } = dataItem;
        lineData.push({
          label,
          x,
          y,
        });
      });
      lineCharts.push({
        colorIndex,
        customLabelComponent,
        data: lineData,
        pattern,
        highLight,
        useLightShadedColor,
        id,
        type: Constants.CHART_ELEMENT_LINE,
        metric,
      });
    });
    return lineCharts;
  };

  const chartHoverHandler = (id, metric, hoverState) => {
    legendHoverHandler(getMetricKey(id, metric), hoverState);
  }

  const createCustomLabelComponent = (styles) => {
    const StyledCustomLabelComponent = CustomLabelComponent(styles);
    return <StyledCustomLabelComponent />;
  };
  
  const legendHoverHandler = (key, hoverState) => {
    setState(prevState => {
      let sharedMutations = prevState.sharedMutations || {};
      const source = getSourceFromMetricKey(key);
      const sourceMutation = (sharedMutations && sharedMutations[source]) || {};
      const metric = getMetricFromMetricKey(key);
      const metricsMutation = (sourceMutation && sourceMutation.metrics) || {};
      const metricMutation = (metricsMutation && metricsMutation[metric]) || {};
      if (hoverState) {
        // DE437921: we started using hover state to enable/disable tooltip
        // But, with VictoryVoronoiContainer data points from different metrics
        // can be in hover state, when they have same values. so, i am
        // clearing hover state of all metrics to keep only the latest one
        // as highlighted(i.e. only it's tooltips will be active).
        // so user would see only 1 tooltip
        sharedMutations = clearMetricsHoverState(sharedMutations);
      }
      const updatedMutations = {
        ...sharedMutations,
        [source]: {
          ...sourceMutation,
          metrics: {
            ...metricsMutation,
            [metric]: {
              ...metricMutation,
              hover: hoverState,
            },
          },
        },
      };
      return {
        sharedMutations: updatedMutations,
      };
    });
  };

  const legendClickHandler = (key, metricEvent) => {
    const {
      timerange,
      timeAgg,
      startDate,
      endDate,
    } = getParamsFromQueryData(defaultErrorsResponse && defaultErrorsResponse.query);
    // get dateRangeData passed, instead of calculating it
    const dateRangeData = getDateRangeData(
      timerange,
      timeAgg,
      startDate,
      endDate,
    );
    const errorsChartData = getErrorRateData(
      dateRangeData,
      defaultErrorsResponse,
    );
    // this shouldn't be required if it is set on props change
    const colorIndex = errorsChartData.find(
      data => {
        const id = key.includes('___') ? key.substring(0, key.indexOf('___')) : key;
        return data.id === id;
      },
    ).colorIndex;
    setState(prevState => {
      const sharedMutations = prevState.sharedMutations || {};
      const sourceKey = metricEvent ? getSourceFromMetricKey(key) : key;
      const sourceMutation = (sharedMutations && sharedMutations[sourceKey]) || {};
      const metricKey = getMetricFromMetricKey(key);
      const metricsMutation = sourceMutation.metrics || {};
      let updatedMutations = sharedMutations;
      if (metricEvent) {
        const metric = getMetricFromMetricKey(key);
        if (metric === ERRORS_METRIC_DEFAULT) {
          updatedMutations = {
            ...sharedMutations,
            [sourceKey]: {
              ...sourceMutation,
              show: !sourceMutation.show,
              metrics: {
                [metricKey]: { show: !sourceMutation.show },
              },
            },
          };
        } else {
          const metricMutation = metricsMutation[metricKey] || {};
          updatedMutations = {
            ...sharedMutations,
            [sourceKey]: {
              show: true,
              metrics: {
                ...metricsMutation,
                default: { show: true },
                [metricKey]: {
                  show: !metricMutation.show,
                },
              },
              colorIndex,
            },
          };
        }
      }
      return {
        sharedMutations: updatedMutations,
        key, metricEvent,
      };
    });
  };

  const yAxisLabel = `${getI18nStrings('errorRateText')} (%)`;
  const chartScale = { x: 'time' };

  const getErrorLegend = (color, isLine, show, pattern) => {
    const legendLineItemClass =
      pattern === 'dashed'
        ? classes.lineLegendItem__dashed
        : classes.lineLegendItem;
    return (<div
      className={
        isLine
          ? legendLineItemClass
          : classes.blockLegendItem
      }
      style={{
        backgroundColor: pattern === 'dashed' ? 'inherit' : color,
        visibility: show ? 'visible' : 'hidden',
        border: pattern === 'dashed' ? `2px dashed ${color}` : 'none',
      }}
    />);
  };

  const getErrorRateLegendData = (
    errorsChartData,
    errorsDataFor30DaysAgo,
    errorsDataFor52WeeksAgo,
  ) => {
    const errorRateLegendData = [];
    const { sharedMutations } = state;
    forEach(errorsChartData, (item, key) => {
      const sourceMutation = sharedMutations[item.id] || {};
      const sourceShow = !!sourceMutation.show;
      const metricsObj = (sourceMutation && sourceMutation.metrics) || {};
      const defaultMetricKey = getMetricKey(item.id, ERRORS_METRIC_DEFAULT);
      const daysMetricKey = getMetricKey(item.id, ERRORS_METRIC_CUSTOM_DATE);
      const weeksMetricKey = getMetricKey(item.id, ERRORS_METRIC_CUSTOM_WEEK);
      const selectedTimerange = defaultErrorsResponse.query.timerange;
      let totalErrorsFor30DaysAgo = '';
      let totalErrorsFor52WeeksAgo = '';
      if (selectedTimerange === 'hour') {
        totalErrorsFor30DaysAgo = getI18nStrings('noDataText');
        totalErrorsFor52WeeksAgo = getI18nStrings('noDataText');
      } else {
        if (is30DaysDataLoading) {
          totalErrorsFor30DaysAgo = 'loading';
        } else {
          totalErrorsFor30DaysAgo =
          errorsDataFor30DaysAgo &&
          errorsDataFor30DaysAgo.find(data => data.id === item.id) &&
          errorsDataFor30DaysAgo.find(data => data.id === item.id).errorRate;
        }
        if (is52WeeksDataLoading) {
          totalErrorsFor52WeeksAgo = 'loading';
        } else {
          totalErrorsFor52WeeksAgo =
          errorsDataFor52WeeksAgo &&
          errorsDataFor52WeeksAgo.find(data => data.id === item.id) &&
          errorsDataFor52WeeksAgo.find(data => data.id === item.id).errorRate;
        }
        if (errorsResponseFor30DaysAgo && !totalErrorsFor30DaysAgo) {
          totalErrorsFor30DaysAgo = getI18nStrings('noDataText');
        }
        if (errorsResponseFor52WeeksAgo && !totalErrorsFor52WeeksAgo) {
          totalErrorsFor52WeeksAgo = getI18nStrings('noDataText');
        }
      }
      const daysMetricShow = !!(
        sourceShow &&
        metricsObj[ERRORS_METRIC_CUSTOM_DATE] &&
        sourceShow &&
        metricsObj[ERRORS_METRIC_CUSTOM_DATE].show
      );
      const weeksMetricShow = !!(
        sourceShow &&
        metricsObj[ERRORS_METRIC_CUSTOM_WEEK] &&
        sourceShow &&
        metricsObj[ERRORS_METRIC_CUSTOM_WEEK].show
      );
      const individualData = [];
      const color = lineColors[key % lineColors.length];
      const lightShadeColor = lightShadeLineColors[key % lightShadeLineColors.length];
      individualData.push(
        {
          id: defaultMetricKey,
          label: 'label.analytics.errorRateChart.errorRate.text',
          value: item.errorRate,
          component: getErrorLegend(color, true, sourceShow),
          show: sourceShow,
          disable: false,
        },
        {
          id: daysMetricKey,
          label: 'label.analytics.trafficChart.legend.30daysLabel.text',
          value: (daysMetricShow && totalErrorsFor30DaysAgo) || '',
          component: getErrorLegend(color, true, daysMetricShow, 'dashed'),
          show: daysMetricShow,
          disable: selectedTimerange === 'hour',
        },
        {
          id: weeksMetricKey,
          label: 'label.analytics.trafficChart.legend.52weeksLabel.text',
          value: (weeksMetricShow && totalErrorsFor52WeeksAgo) || '',
          component: getErrorLegend(lightShadeColor, true, weeksMetricShow),
          show: weeksMetricShow,
          disable: selectedTimerange === 'hour',
        },
      );
      errorRateLegendData.push({
        uuid: item.id,
        names: item.names,
        color,
        data: individualData,
        suffix: '%',
        show: sourceShow,
      });
    });

    return errorRateLegendData;
  };

  const getXAxisFormatter = (item, dateRangeData) => {
    const xAxisTickValuesMap = dateRangeData.xAxisTickValuesMap;
    if (xAxisTickValuesMap[item]) {
      return getDisplayDate(xAxisTickValuesMap[item], dateRangeData.dateRange,
        dateRangeData.timeAgg, dateRangeData.startDate, dateRangeData.endDate);
    }
    return '';
  };

  const { timerange, timeAgg, startDate, endDate } = getParamsFromQueryData(
    defaultErrorsResponse &&
      defaultErrorsResponse.query,
  );

  const dateRangeData = getDateRangeData(timerange, timeAgg, startDate, endDate);
  const errorRateChartData = getErrorRateData(
    dateRangeData,
    defaultErrorsResponse,
  );
  const lineChartsData = getLineChartsData(errorRateChartData, ERRORS_METRIC_DEFAULT) || [];
  let errorRateLegendData = getErrorRateLegendData(errorRateChartData);
  const historicalChartDataFor30Days = [];
  const historicalChartDataFor52Weeks = [];
  if (errorsResponseFor30DaysAgo) {
    historicalChartDataFor30Days.push(...getHistoricalChartDataFromResponse(
      errorsResponseFor30DaysAgo),
    );
    const lineChartDataFor30Days = getLineChartsData(
      historicalChartDataFor30Days,
      ERRORS_METRIC_CUSTOM_DATE,
    );
    lineChartsData.push(...lineChartDataFor30Days);
  }
  if (errorsResponseFor52WeeksAgo) {
    historicalChartDataFor52Weeks.push(...getHistoricalChartDataFromResponse(
      errorsResponseFor52WeeksAgo),
    );
    const lineChartDataFor52Weeks = getLineChartsData(
      historicalChartDataFor52Weeks,
      ERRORS_METRIC_CUSTOM_WEEK,
    );
    lineChartsData.push(...lineChartDataFor52Weeks);
  }
  if (historicalChartDataFor52Weeks.length || historicalChartDataFor30Days.length) {
    errorRateLegendData = getErrorRateLegendData(
      errorRateChartData,
      historicalChartDataFor30Days,
      historicalChartDataFor52Weeks,
    );
  }
  const renderChartComponent = () => {
    if (isLoading) {
      return (
        <div className={classes.loader}>
          <Loading />
        </div>
      );
    }

    return (
      <div className={classes.chartContainer}>
        <div className={classes.lineChartContainer}>
          <div className={classes.plcaeholderText}>
            {intl.getI18nMessage('label.analytics.errorRateChart.placeholder.title.text')}
          </div>
          <CompositeXYChart
            chartData={lineChartsData}
            chartScale={chartScale}
            formatXAxisData={(item) => getXAxisFormatter(item, dateRangeData)}
            hoverHandler={chartHoverHandler}
            lightShadeLineColors={lightShadeLineColors}
            lineColors={lineColors}
            xAxisLabel={getI18nStrings('dateTimeFormat')}
            xAxisValues={dateRangeData.xAxisTickValues}
            yAxisLabel={yAxisLabel}
          />
        </div>
        <div className={clsx(classes.labelForPrint, classes.errorRateChartLabel)}>
          {intl.getI18nMessage('label.analytics.lineChart.metric.text')}
        </div>
        <ChartLegend
          data={errorRateLegendData}
          hoverHandler={legendHoverHandler}
          clickHandler={legendClickHandler}
        />
      </div>
    )
  };

  return (
    <div
      className={classes.errorRateChartContainer}
      data-apim-test="chart-error-rate"
    >
      <div className={classes.errorRateChartLabel}>
        <div className={classes.chartTitle}>
          {intl.getI18nMessage('label.analytics.errorRateChart.errorRate.text')}
        </div>
        <div className={classes.metricsLabel}>
          {intl.getI18nMessage('label.analytics.lineChart.metric.text')}
        </div>
      </div>
      {!errorRateChartData.length && !isLoading ? <NoData /> : renderChartComponent()}
    </div>
  );
}

ErrorRateChart.propTypes = {
  defaultErrorsResponse: PropTypes.array,
  apiMap: PropTypes.shape(),
  appMap: PropTypes.shape(),
  orgMap: PropTypes.shape(),
  isLoading: PropTypes.bool,
  fetchCustomDateMetricsData: PropTypes.func,
  errorsResponseFor30DaysAgo: PropTypes.object,
  errorsResponseFor52WeeksAgo: PropTypes.object,
  is30DaysDataLoading: PropTypes.bool,
  is52WeeksDataLoading: PropTypes.bool,
};

export default ErrorRateChart;
